import { useState, type ChangeEvent } from "react";

import { Pencil, Trash2 } from "lucide-react";

import { AlertV4, ButtonV4, ChipV4, SelectV4 } from "../common/ui";
import formatComma from "react-lib/utils/formatComma";
import type { OnEventHandler } from "react-lib/appcon/common/SearchBoxDropdown";
import type { DoctorOption, Icd9Option, OrderMasterOptions } from "../common/sequence/OrderSheetV4";

import {
  CATEGORY_LABELS,
  CATEGORY_TONES,
  URGENCY_LABELS,
  formatTat,
  isCapsuleDrug,
  labDestinationOptions,
  pickOfLine,
  type LabTest,
  type PickItem,
  type SheetCategory,
  type SheetRow,
} from "./ordersModelV4";
import { isCostCenter, sheetMode } from "./orderSheetLineModelV4";
import { SheetLineControls, SheetLineFields } from "./OrderSheetLineV4";
import { DrugIcon, StarButton } from "./OrderLineIconsV4";

// Types
export type SaveScope = "mine" | "clinic";

export type SaveSetForm = { open: boolean; name: string; scope: SaveScope };

type PendingGroup = {
  category: SheetCategory;
  lines: SheetRow[];
  labText: string;
  subtotal: number;
};

type SheetLineChange = { quantity?: number; priceUnit?: number; fields?: Record<string, string> };

type OrderPendingSheetV4Props = {
  // data
  groups: PendingGroup[];
  draftItems: any[];
  labTestInfo: Record<string, LabTest>;
  openGroups: Partial<Record<SheetCategory, boolean>>;
  openLines: Record<string, boolean>;
  favKeys: Set<string>;
  doctorOptions: DoctorOption[];
  masterOptions?: OrderMasterOptions;
  icd9Options: Icd9Option[];
  searchedItemListWithKey?: Record<string, any>;
  hasDone: boolean;
  pendingCount: number;
  pendingTotal: number;
  saveSet: SaveSetForm;
  isSavingSet: boolean;
  saveSetError?: unknown;
  // callback
  onEvent: OnEventHandler;
  onToggleGroup: (category: SheetCategory) => void;
  onToggleLine: (key: string) => void;
  onChangeSheetLine: (key: string, data: SheetLineChange) => void;
  onRemoveSheetLine: (key: string) => void;
  onChangeItem: (index: number, field: "urgency" | "note", value: string) => void;
  onDeleteItem: (index: number) => void;
  onToggleFavourite: (pick?: PickItem) => void;
  onSearchIcd9: (query: string) => void;
  onChangeSaveSet: (form: SaveSetForm) => void;
  onSaveSet: () => void;
  onCloseSaveSetError: () => void;
};

// Const
const URGENCY_OPTIONS = Object.entries(URGENCY_LABELS).map(([value, label]) => ({
  value,
  text: label,
}));

const SCOPE_OPTIONS = [
  { value: "mine", text: "ของฉัน" },
  { value: "clinic", text: "ของคลินิก" },
];

/** รายการรอยืนยันคำสั่ง แยกกลุ่มตามประเภท + บันทึกเป็น Order set */
const OrderPendingSheetV4 = (props: OrderPendingSheetV4Props) => {
  const [openKids, setOpenKids] = useState<Record<string, boolean>>({});

  const { saveSet } = props;

  // Handler
  const handleToggleSaveSet = () => {
    props.onChangeSaveSet({ ...saveSet, open: !saveSet.open });
  };

  const handleChangeSetName = (e: ChangeEvent<HTMLInputElement>) => {
    props.onChangeSaveSet({ ...saveSet, name: e.target.value });
  };

  const handleChangeSetScope = (value: string) => {
    props.onChangeSaveSet({ ...saveSet, scope: value as SaveScope });
  };

  // Render
  const renderLabCard = (line: SheetRow, item: any, index: number, test?: LabTest) => (
    <div className="hv4-ord-lab-card">
      <SelectV4
        className="hv4-ord-lab-pri"
        aria-label="ความเร่งด่วน"
        options={URGENCY_OPTIONS}
        value={item.urgency}
        onChange={(value) => props.onChangeItem(index, "urgency", value)}
      />
      <span>ส่งตรวจ:</span>
      <SelectV4
        className="hv4-ord-lab-dest"
        aria-label="ส่งตรวจ"
        options={labDestinationOptions(item.lab_type_label)}
        value={item.lab_type_label === "Out lab" ? "OUT" : "IN"}
        onChange={() => undefined}
      />
      {!!formatTat(test) && <span className="hv4-ord-lab-tat">TAT ~{formatTat(test)}</span>}
      <input
        className="hv4-input hv4-ord-lab-note"
        aria-label="หมายเหตุ"
        placeholder="หมายเหตุ (ข้อมูลทางคลินิก)"
        value={item.note ?? ""}
        onChange={(e) => props.onChangeItem(index, "note", e.target.value)}
      />
      <ButtonV4 variant="primary" onClick={() => props.onToggleLine(line.key)}>
        บันทึก
      </ButtonV4>
    </div>
  );

  const renderLine = (line: SheetRow) => {
    const index = line.draftIndex;
    const item = index === undefined ? undefined : props.draftItems[index];
    const test = item ? props.labTestInfo[item.lab_code] : undefined;
    const draft = line.draft;
    const isDraft = index !== undefined || !!draft;
    const isLineOpen = !!props.openLines[line.key];
    const mode = draft ? sheetMode(draft) : null;
    const pick = pickOfLine(line, test);
    const kids = test?.children ?? [];
    const isKidsOpen = kids.length > 0 && !!openKids[line.key];
    const kidsTitle = `พารามิเตอร์ย่อย ${kids.length} รายการ`;

    return (
      <div key={line.key} id={`hv4-ord-line-${line.key}`} className="hv4-ord-line">
        <div className="hv4-ord-line-row">
          <div className="hv4-ord-line-main">
            <div className="hv4-ord-line-name">
              {draft?.kind === "drug" && <DrugIcon isCapsule={isCapsuleDrug(draft.raw.detail)} />}
              <b>{line.name}</b>
              {!!line.missing?.length && (
                <ChipV4 size="sm" bold tone="warn-solid">
                  ต้องระบุ {line.missing.join(", ")}
                </ChipV4>
              )}
              {!!mode && (
                <ChipV4 size="sm" bold tone={mode[1]}>
                  {mode[0]}
                </ChipV4>
              )}
              {line.outLab && (
                <ChipV4 size="sm" bold tone="purple">
                  Out lab
                </ChipV4>
              )}
              {kids.length > 0 && (
                <button
                  type="button"
                  className="hv4-ord-kids-btn"
                  aria-expanded={isKidsOpen}
                  aria-label={kidsTitle}
                  title={kidsTitle}
                  onClick={() => setOpenKids({ ...openKids, [line.key]: !isKidsOpen })}
                >
                  ···
                </button>
              )}
            </div>
            {!!line.summary && <div className="hv4-ord-line-summary">{line.summary}</div>}
            {!!line.pkgText && <div className="hv4-ord-line-pkg">📦 {line.pkgText}</div>}
            {!!line.doseWarning && <div className="hv4-ord-line-dose">⚠ {line.doseWarning}</div>}
          </div>
          <div className="hv4-ord-line-side">
            {!!draft && (
              <SheetLineControls
                line={draft}
                onChange={(data) => props.onChangeSheetLine(draft.key, data)}
              />
            )}
            <b className="hv4-ord-line-amount">
              {line.pkgText
                ? "0 (ใช้สิทธิ์แพ็กเกจ)"
                : draft && isCostCenter(draft)
                  ? "0 (เหมารวม)"
                  : formatComma(line.amount, "en-US", false)}
            </b>
            {isDraft && (
              <>
                {!!pick && (
                  <StarButton
                    name={line.name}
                    isOn={props.favKeys.has(pick.key)}
                    onClick={() => props.onToggleFavourite(pick)}
                  />
                )}
                <ButtonV4
                  variant="ghost"
                  size="icon"
                  selected={isLineOpen}
                  aria-expanded={isLineOpen}
                  aria-label={`${isLineOpen ? "ปิดรายละเอียด" : "แก้ไข"} ${line.name}`}
                  title={`${isLineOpen ? "ปิดรายละเอียด" : "แก้ไข"} ${line.name}`}
                  onClick={() => props.onToggleLine(line.key)}
                >
                  <Pencil size={18} aria-hidden="true" />
                </ButtonV4>
                <ButtonV4
                  variant="danger-ghost"
                  size="icon"
                  aria-label={`ลบ ${line.name}`}
                  title={`ลบ ${line.name}`}
                  onClick={() =>
                    draft
                      ? props.onRemoveSheetLine(draft.key)
                      : index !== undefined && props.onDeleteItem(index)
                  }
                >
                  <Trash2 size={18} aria-hidden="true" />
                </ButtonV4>
              </>
            )}
          </div>
        </div>

        {!!line.adr && (
          <div className="hv4-ord-line-adr">
            แพ้ยา ({line.adr.adversary}) · แพทย์ยืนยันสั่งต่อ: {line.adr.reason || "-"}
          </div>
        )}

        {isKidsOpen && (
          <div className="hv4-ord-kids" aria-label={kidsTitle}>
            {kids.map((kid) => (
              <ChipV4 key={kid.id ?? kid.name} size="sm" tone="purple">
                {kid.name}
              </ChipV4>
            ))}
          </div>
        )}

        {!!draft && isLineOpen && (
          <SheetLineFields
            line={draft}
            doctorOptions={props.doctorOptions}
            masterOptions={props.masterOptions}
            icd9Options={props.icd9Options}
            searchedItemListWithKey={props.searchedItemListWithKey}
            onEvent={props.onEvent}
            onChange={(fields) => props.onChangeSheetLine(draft.key, { fields })}
            onSearchIcd9={props.onSearchIcd9}
            onClose={() => props.onToggleLine(line.key)}
          />
        )}

        {!!item && index !== undefined && isLineOpen && renderLabCard(line, item, index, test)}
      </div>
    );
  };

  const renderGroup = (group: PendingGroup) => {
    const isOpen = props.openGroups[group.category] !== false;

    return (
      <div key={group.category} className="hv4-ord-group">
        <button
          type="button"
          aria-expanded={isOpen}
          className="hv4-ord-group-head"
          onClick={() => props.onToggleGroup(group.category)}
        >
          <span className="hv4-ord-chev">{isOpen ? "▾" : "▸"}</span>
          <ChipV4 size="sm" tone={CATEGORY_TONES[group.category]}>
            {CATEGORY_LABELS[group.category]}
          </ChipV4>
          <span className="hv4-ord-group-count">{group.lines.length} รายการ</span>
          {!!group.labText && <span className="hv4-ord-group-lab">{group.labText}</span>}
          <b className="hv4-ord-group-sub">{formatComma(group.subtotal, "en-US", false)} ฿</b>
        </button>

        {isOpen && group.lines.map(renderLine)}
      </div>
    );
  };

  const renderSaveSet = () => (
    <div className="hv4-ord-save-set">
      <input
        className="hv4-input"
        aria-label="ชื่อ Order set"
        placeholder="ชื่อ Order set"
        value={saveSet.name}
        onChange={handleChangeSetName}
      />
      <SelectV4
        className="hv4-ord-save-set-scope"
        aria-label="เก็บเป็น"
        options={SCOPE_OPTIONS}
        value={saveSet.scope}
        onChange={handleChangeSetScope}
      />
      <ButtonV4
        variant="ok"
        disabled={!saveSet.name.trim() || props.isSavingSet}
        onClick={props.onSaveSet}
      >
        บันทึก
      </ButtonV4>
    </div>
  );

  return (
    <section className="hv4-ord-sheet">
      <h2 className="hv4-ord-sheet-title">รายการรอยืนยันคำสั่ง</h2>
      <p className="hv4-ord-sheet-desc">
        พักรายการที่เลือกไว้ก่อน · กด "ยืนยันคำสั่ง" แล้วรายการจะย้ายไปอยู่ในส่วน
        คำสั่งที่ยืนยันแล้ว ด้านบน
      </p>

      <div className="hv4-ord-groups">
        {props.groups.map(renderGroup)}

        {props.groups.length === 0 && (
          <div className="hv4-ord-empty">
            {props.hasDone
              ? "ไม่มีรายการรอยืนยัน · รายการที่ยืนยันแล้วอยู่ในส่วน คำสั่งที่ยืนยันแล้ว ด้านบน · เพิ่มรายการใหม่ได้จากช่องค้นหา"
              : "ยังไม่มีรายการ · ค้นหารายการ หรือเลือกจาก Order Set / รายการโปรด / Personalized"}
          </div>
        )}
      </div>

      <div className="hv4-ord-sheet-total">
        <ButtonV4
          variant="ghost"
          aria-expanded={saveSet.open}
          disabled={props.pendingCount === 0}
          onClick={handleToggleSaveSet}
        >
          บันทึกเป็น Order set
        </ButtonV4>
        <span>
          {props.pendingCount} รายการ · รวม <b>{formatComma(props.pendingTotal, "en-US", false)}</b>{" "}
          บาท
        </span>
      </div>
      {saveSet.open && props.pendingCount > 0 && renderSaveSet()}
      {!!props.saveSetError && (
        <AlertV4 tone="danger" error={props.saveSetError} onClose={props.onCloseSaveSetError}>
          บันทึก Order set ไม่ได้:{" "}
        </AlertV4>
      )}
    </section>
  );
};

OrderPendingSheetV4.displayName = "OrderPendingSheetV4";

export default OrderPendingSheetV4;
